import { ReactNode, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import currency from 'currency.js'
import { times } from 'lodash'

import { Text, Button, Popup, Accordion, Separator, Dialog } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import {
  centsToCurrency,
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectTaxCalculationOutput,
  selectUserTaxCalculationById,
} from '@/selectors/userTaxCalculation.selectors'
import {
  getUserIsAdmin,
  selectIsCurrentUserScorp,
  selectIsFilingJointly,
} from '@/selectors/user.selectors'
import {
  AnnualGrossIncomeTooltip,
  AnnualizedPPNetProfitTooltip,
  AnnualizedW2IncomeTooltip,
  AnnualTaxLiabilityNetTooltip,
  AnnualTaxLiabilityTooltip,
  TaxableIncomeTooltip,
  TotalPreviousQTEPaymentsTooltip,
  YTD1099IncomeTooltip,
  YTDTaxLiabilityGrossTooltip,
  YTDTaxLiabilityNetTooltip,
} from './CalculationOutputTooltips'

import { UserTaxCalculation } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'

const CalculationRow = ({
  title,
  value,
  popupContent,
}: {
  title: string
  value: string
  popupContent?: ReactNode
}) => (
  <div style={{ display: 'flex', marginBottom: 10 }}>
    {popupContent ? (
      <Popup
        content={popupContent}
        trigger={<Text className="underline text-green-600">{title}</Text>}
      />
    ) : (
      <Text>{title}</Text>
    )}

    <div className="border-b border-dotted border-neutral-700 flex-1 mx-2 mb-2" />
    <Text>{value}</Text>
  </div>
)

export const CalcBreakdownSection = ({
  calcId,
  isState,
}: {
  calcId: number
  isState?: boolean
}) => {
  const isAdmin = useReselector(getUserIsAdmin)
  // Right now this just affects tooltip content so don't worry about what admins see
  const isScorp = useReselector(selectIsCurrentUserScorp)
  // Just show spouse if this is admin viewing this user
  const showSpouse = useReselector(selectIsFilingJointly) || isAdmin

  const calculation = useReselector(selectUserTaxCalculationById, calcId)
  const calculationOutput = useReselector(selectTaxCalculationOutput, calcId)

  const {
    annualizedTotalWIncomeInCents,
    otherIndividualIncomeInCents,
    spouseIndividualIncomeInCents,
    federalWithholdingInCents,
    spouseFederalWithholdingInCents,
    stateWithholdingInCents,
    spouseStateWithholdingInCents,
    annualizedIncomeInCents,
    annualizedExpensesInCents,
    calendarQuarter,
    federalQuarterlyEstimateInCents,
    stateQuarterlyEstimates,
  } = calculation || {}

  const {
    annualGrossIncomeInCents,
    annualTaxableIncomeInCents,
    annualFederalIncomeTaxLiabilityInCents,
    annualStateTaxLiabilityInCents,
    annualSelfEmploymentTaxLiabilityInCents,
    allFederalQuarterPaymentsLogged,
    allStateQuarterPaymentsLogged,
    YTDFederalTaxPaymentsMadeInCents,
    YTDStateTaxPaymentsMadeInCents,
    YTDGrossFederalTaxLiabilityInCents,
    YTDGrossStateTaxLiabilityInCents,
    annualTotalFederalWithholdingInCents,
    annualTotalStateWithholdingInCents,
    annualFederalEstimateInCents,
    annualStateEstimateInCents,
    quarterFederalEstimateInCents,
    quarterStateEstimateInCents,
  } = calculationOutput?.data || {}

  const stateName = stateQuarterlyEstimates?.[0].state

  const prevQuarterText = useMemo(
    () =>
      calendarQuarter
        ? ` (${times(Number(calendarQuarter) - 1)
            .map((num) => `Q${num + 1}`)
            .join(', ')})`
        : '',
    [calendarQuarter]
  )

  // todo this needs to be updated to support Q4
  const ytdTaxLiability = useMemo(() => {
    if (!isState) {
      if (allFederalQuarterPaymentsLogged) {
        return currency(
          centsToCurrency(YTDGrossFederalTaxLiabilityInCents)
        ).subtract(centsToCurrency(annualTotalFederalWithholdingInCents))
      }

      return centsToCurrency(annualFederalEstimateInCents)
    }

    if (allStateQuarterPaymentsLogged) {
      return currency(
        centsToCurrency(YTDGrossStateTaxLiabilityInCents)
      ).subtract(centsToCurrency(annualTotalStateWithholdingInCents))
    }

    return centsToCurrency(annualStateEstimateInCents)
  }, [
    YTDGrossFederalTaxLiabilityInCents,
    YTDGrossStateTaxLiabilityInCents,
    allFederalQuarterPaymentsLogged,
    allStateQuarterPaymentsLogged,
    annualFederalEstimateInCents,
    annualStateEstimateInCents,
    annualTotalFederalWithholdingInCents,
    annualTotalStateWithholdingInCents,
    isState,
  ])

  const estType = isState ? `${stateName} State` : 'Federal'

  const allPaymentsProvided = isState
    ? allStateQuarterPaymentsLogged
    : allFederalQuarterPaymentsLogged

  return (
    <div className="flex flex-col gap-4">
      <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
        Taxable Income
      </Text>

      <div className="flex flex-col md:flex-row gap-2 md:items-start">
        <div className="flex-1">
          <Accordion type="single" collapsible variant="text" className="mt-0">
            <Accordion.Item value="annual-gross-income">
              <Accordion.Trigger>
                <Popup
                  content={<AnnualGrossIncomeTooltip />}
                  trigger={
                    <Text color={TextColors.GREEN} className="underline">
                      Annual Gross Income
                    </Text>
                  }
                />
              </Accordion.Trigger>
              <Accordion.Content>
                <Text className="mb-2">
                  Every quarter we reforecast your quarterly tax estimate based
                  on your Profit & Loss Statement (PnL) and your income. Annual
                  Gross Income is the calculated sum of:
                </Text>
                <CalculationRow
                  title="Annualized Private Practice Net Profit"
                  value={formatCurrencyFromCents(
                    centsToCurrency(annualizedIncomeInCents).subtract(
                      centsToCurrency(annualizedExpensesInCents)
                    )
                  )}
                  popupContent={<AnnualizedPPNetProfitTooltip />}
                />
                <CalculationRow
                  title="Annualized Total W-2 Income"
                  value={formatCurrencyFromCents(annualizedTotalWIncomeInCents)}
                  popupContent={<AnnualizedW2IncomeTooltip isScorp={isScorp} />}
                />
                <CalculationRow
                  title="YTD 1099 Income"
                  value={formatCurrencyFromCents(otherIndividualIncomeInCents)}
                  popupContent={<YTD1099IncomeTooltip />}
                />
                {showSpouse && (
                  <CalculationRow
                    title="YTD Spouse 1099 Income"
                    value={formatCurrencyFromCents(
                      spouseIndividualIncomeInCents
                    )}
                  />
                )}
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </div>
        <Text className="w-full md:w-1/3 text-right">
          {formatCurrencyFromCents(annualGrossIncomeInCents)}
        </Text>
      </div>

      {!isState && (
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
          <div className="flex-1">
            <Popup
              content={<TaxableIncomeTooltip />}
              trigger={<Text color={TextColors.GREEN}>Taxable Income</Text>}
            />
          </div>
          <Text className="w-full md:w-1/3 text-right">
            {formatCurrencyFromCents(annualTaxableIncomeInCents)}
          </Text>
        </div>
      )}

      <Separator />

      <div className="flex flex-col md:flex-row items-start gap-2 bg-stone-100 px-4 py-2 rounded-md">
        <div className="flex-1">
          <Popup
            content={<AnnualTaxLiabilityTooltip isState={Boolean(isState)} />}
            trigger={
              <Text color={TextColors.GREEN} className="underline">
                Annual Tax Liability (Gross)
              </Text>
            }
          />
        </div>
        <Text className="w-full md:w-1/3 text-right">
          {formatCurrencyFromCents(annualStateTaxLiabilityInCents)}
        </Text>
        {isState
          ? formatCurrencyFromCents(annualStateTaxLiabilityInCents)
          : formatCurrencyFromCents(
              currency(
                centsToCurrency(annualFederalIncomeTaxLiabilityInCents)
              ).add(centsToCurrency(annualSelfEmploymentTaxLiabilityInCents))
            )}
      </div>

      <Separator />

      <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
        {estType} Quarterly Tax Estimate Calculation
      </Text>

      {allPaymentsProvided ? (
        <>
          {/* YTD Tax Liability (Gross) */}
          <div className="flex flex-col md:flex-row items-start gap-2">
            <div className="flex-1">
              <Popup
                content={<YTDTaxLiabilityGrossTooltip />}
                trigger={
                  <Text color={TextColors.GREEN} className="underline">
                    YTD Tax Liability (Gross)
                  </Text>
                }
              />
            </div>
            <Text className="w-full md:w-1/3 text-right">
              {formatCurrencyFromCents(
                isState
                  ? YTDGrossStateTaxLiabilityInCents
                  : YTDGrossFederalTaxLiabilityInCents
              )}
            </Text>
          </div>

          {/* YTD Tax Liability (Net) */}
          <div className="flex flex-col md:flex-row items-start gap-2">
            <div className="flex-1">
              <Accordion
                type="single"
                collapsible
                variant="text"
                className="mt-0"
              >
                <Accordion.Item value="ytd-tax-liability-net">
                  <Accordion.Trigger>
                    <Popup
                      content={<YTDTaxLiabilityNetTooltip />}
                      trigger={
                        <Text color={TextColors.GREEN} className="underline">
                          YTD Tax Liability (Net)
                        </Text>
                      }
                    />
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <Text className="mb-2">
                      We subtract your withholdings (and your spouse&apos;s, if
                      applicable) from your Gross YTD Tax Liability to get your
                      Net YTD Tax Liability.
                    </Text>

                    {isState ? (
                      <>
                        <CalculationRow
                          title="Your YTD State Withholdings"
                          value={formatCurrencyFromCents(
                            stateWithholdingInCents
                          )}
                        />
                        {showSpouse && (
                          <CalculationRow
                            title="Spouse YTD State Withholdings"
                            value={formatCurrencyFromCents(
                              spouseStateWithholdingInCents
                            )}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <CalculationRow
                          title="Your YTD Federal Withholdings"
                          value={formatCurrencyFromCents(
                            federalWithholdingInCents
                          )}
                        />
                        {showSpouse && (
                          <CalculationRow
                            title="Spouse YTD Federal Withholdings"
                            value={formatCurrencyFromCents(
                              spouseFederalWithholdingInCents
                            )}
                          />
                        )}
                      </>
                    )}
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion>
            </div>
            <Text className="w-full md:w-1/3 text-right">
              {formatCurrency(ytdTaxLiability)}
            </Text>
          </div>

          {/* Total Previous QTE Payments */}
          <div className="flex flex-col md:flex-row items-start gap-2">
            <div className="flex-1">
              <Popup
                content={<TotalPreviousQTEPaymentsTooltip />}
                trigger={
                  <Text color={TextColors.GREEN} className="underline">
                    Total previous quarterly tax estimate payments{' '}
                    {prevQuarterText}
                  </Text>
                }
              />
            </div>
            <Text className="w-full md:w-1/3 text-right">
              {formatCurrencyFromCents(
                isState
                  ? YTDStateTaxPaymentsMadeInCents
                  : YTDFederalTaxPaymentsMadeInCents
              )}
            </Text>
          </div>
        </>
      ) : (
        <div className="flex flex-col md:flex-row items-start gap-2">
          <div className="flex-1">
            <Popup
              content={<AnnualTaxLiabilityNetTooltip />}
              trigger={
                <Text color={TextColors.GREEN} className="underline">
                  Annual Tax Liability (Net)
                </Text>
              }
            />
          </div>
          <Text className="w-full md:w-1/3 text-right">
            {formatCurrency(ytdTaxLiability)}
          </Text>
        </div>
      )}

      {isAdmin && (
        <div className="flex flex-col md:flex-row items-start gap-2">
          <div className="flex-1">
            <Text>
              QTE Calc Est Output (this line not shared with customer)
            </Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              <i>
                {allPaymentsProvided
                  ? 'Method: Smoothed (previous quarterly tax payments provided)'
                  : 'Method: Divide by 4 (one or more quarterly tax payments not provided)'}
              </i>
            </Text>
          </div>
          <div className="w-full md:w-1/3 text-right">
            <Text>
              {formatCurrencyFromCents(
                isState
                  ? quarterStateEstimateInCents
                  : quarterFederalEstimateInCents
              )}
            </Text>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col md:flex-row items-center gap-2 bg-pink-100 p-6 rounded-md">
        <div className="flex-1">
          <Text variant={TypographyStyles.H3}>
            {estType} Quarterly Tax Estimate
          </Text>
        </div>
        <div className="w-full md:w-1/3 text-right">
          <Text variant={TypographyStyles.H3}>
            {isState
              ? formatCurrency(stateQuarterlyEstimates?.[0].value)
              : formatCurrencyFromCents(federalQuarterlyEstimateInCents)}
          </Text>
        </div>
      </div>
    </div>
  )
}

export const CalcBreakdown = ({ calcId }: { calcId: number }) => {
  const calculation = useReselector(selectUserTaxCalculationById, calcId)
  const [searchParams] = useSearchParams()

  const section = searchParams.get('section')

  if (!calculation) {
    return null
  }

  return (
    <Accordion
      type="single"
      collapsible
      variant="text"
      defaultValue={
        section === 'federal'
          ? 'federal'
          : section === 'state'
            ? 'state'
            : undefined
      }
    >
      <Accordion.Item value="federal">
        <Accordion.Trigger>
          <Text variant={TypographyStyles.H2}>
            Federal estimate calculation
          </Text>
        </Accordion.Trigger>
        <Accordion.Content>
          <CalcBreakdownSection calcId={calcId} />
        </Accordion.Content>
      </Accordion.Item>

      <Accordion.Item value="state">
        <Accordion.Trigger>
          <Text variant={TypographyStyles.H2}>
            {calculation.stateQuarterlyEstimates?.[0].state} State estimate
            calculation
          </Text>
        </Accordion.Trigger>
        <Accordion.Content>
          <CalcBreakdownSection calcId={calcId} isState />
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  )
}

export const CalculationOutputsModal = ({
  calc,
}: {
  calc: UserTaxCalculation
}) => {
  const calculationOutput = useReselector(selectTaxCalculationOutput, calc.id)

  const [open, setOpen] = useState(false)

  if (!calculationOutput) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button size="md" className="my-2">
          View QTE Details
        </Button>
      </Dialog.Trigger>

      <Dialog.Content className="sm:max-w-lg">
        <Dialog.Title>Tax Calculation Output</Dialog.Title>

        <div className="py-2">
          <CalcBreakdown calcId={calc.id} />
        </div>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button onClick={() => setOpen(false)} variant="secondary">
              Cancel
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
